import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

// Social Media SVG Icons - LARGER SIZE
const TwitterIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLanguage();
  const t = familyTranslations[lang].footerData;
  const isMemorianPage = location.pathname === '/become-memorian' || location.pathname === '/become-memorian';

  const handleNavClick = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="border-t border-gray-100 bg-white">
      {/* Cross-link banner */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-center gap-3">
          {isMemorianPage ? (
            <>
              <span className="text-sm text-gray-500">{t.crossLinkFamily}</span>
              <button
                onClick={() => navigate('/')}
                className="text-sm font-semibold text-[#5B4FE9] hover:text-[#4A3ED8] transition-colors duration-200"
              >
                {t.crossLinkFamilyCta} &rarr;
              </button>
            </>
          ) : (
            <>
              <span className="text-sm text-gray-500">{t.crossLinkMemorian}</span>
              <button
                onClick={() => navigate('/become-memorian')}
                className="text-sm font-semibold text-[#5B4FE9] hover:text-[#4A3ED8] transition-colors duration-200"
              >
                {t.crossLinkMemorianCta} &rarr;
              </button>
            </>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Main footer content - make social media more prominent */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start lg:items-center">
          {/* Brand - Left */}
          <div className="flex-shrink-0">
            <a href="#" className="flex items-center select-none">
              <div className="flex items-center gap-2.5">
                <svg width="35" height="47" viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                  <path d="M5 16C5 7.71573 11.7157 1 20 1C28.2843 1 35 7.71573 35 16V39H5V16Z" fill="#1f2937"/>
                  <rect x="2" y="43" width="36" height="6" fill="#1f2937"/>
                </svg>
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-gray-800 tracking-wide leading-tight" style={{ fontFamily: 'Arial, sans-serif', letterSpacing: '0.05em' }}>BEYOND MEMORIA</span>
                  <span className="text-[9px] text-gray-600 tracking-wide uppercase" style={{ fontFamily: 'Arial, sans-serif', letterSpacing: '0.08em', marginTop: '-2px' }}>{familyTranslations[lang].logoTagline}</span>
                </div>
              </div>
            </a>
            <p className="mt-1 text-sm text-gray-400">{t.copyright}</p>
          </div>

          {/* Links - Center */}
          <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-6">
            {t.links.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => navigate('/privacy')}
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200"
            >
              {lang === 'fr' ? 'Confidentialité' : 'Privacy'}
            </button>
            <button
              onClick={() => navigate('/terms')}
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200"
            >
              {lang === 'fr' ? 'CGU' : 'Terms'}
            </button>
          </div>

          {/* Social Media - Right - PROMINENT */}
          <div className="flex items-center justify-start lg:justify-end gap-3 order-first lg:order-last">
            <span className="text-sm text-gray-600 font-medium mr-2">Follow us:</span>
            {/* Facebook */}
            <a
              href="https://www.facebook.com/beyond.memoria"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center hover:bg-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
              aria-label="Facebook"
            >
              <FacebookIcon />
            </a>
            {/* TikTok */}
            <a
              href="https://www.tiktok.com/@beyond.memoria"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:bg-gray-900 transition-all duration-200 shadow-md hover:shadow-lg"
              aria-label="TikTok"
            >
              <TikTokIcon />
            </a>
            {/* Twitter/X */}
            <a
              href="https://x.com/beyondmemoria"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-black transition-all duration-200 shadow-md hover:shadow-lg"
              aria-label="X (Twitter)"
            >
              <TwitterIcon />
            </a>
            {/* LinkedIn */}
            <a
              href="https://www.linkedin.com/company/beyondmemoria/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg"
              aria-label="LinkedIn"
            >
              <LinkedInIcon />
            </a>
          </div>
        </div>
      </div>

      {/* Advaltis Credit */}
      <div className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3">
          <p className="text-center text-xs text-gray-500">
            Beyond Memoria - Powered by <span className="font-semibold text-gray-700">Advaltis Limited</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
